import React, { useState, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest, APPROVERS } from "./authConfig";
import { setUserContext } from "./api";
import ExpenseReport from "./components/ExpenseReport";
import ReportHistory from "./components/ReportHistory";
import ApprovalView from "./components/ApprovalView";
import MyImages from "./components/MyImages";
import Toast from "./components/Toast";

// Simple toast context
export const ToastContext = React.createContext(null);

function LoginScreen({ onLogin }) {
  return (
    <div className="login-screen">
      <div className="login-logo">
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polygon points="36,4 68,36 36,68 4,36" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.6)" strokeWidth="2"/>
          <polygon points="36,14 58,36 36,58 14,36" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5"/>
          <text x="36" y="41" textAnchor="middle" fill="white" fontSize="18" fontWeight="700" fontFamily="-apple-system,sans-serif">LC</text>
        </svg>
      </div>
      <p className="login-brand">The Lake Companies</p>
      <h1 className="login-title">The Lake Companies Expense Report App</h1>
      <p className="login-sub">
        Sign in with your Lake Companies account to submit and track expense reports.
      </p>
      <button className="login-btn" onClick={onLogin}>
        <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
          <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
          <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
        </svg>
        Sign in with Microsoft
      </button>
    </div>
  );
}

export default function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [activeTab, setActiveTab] = useState("submit");
  const [toast, setToast] = useState(null);

  const user = accounts[0];
  const isApprover = user && APPROVERS.includes(user.username?.toLowerCase());

  // Set synchronously so API headers are ready before child effects fire
  if (user) {
    setUserContext(user.username || user.upn || "", user.name || "");
  }

  const [expenseModalTrigger, setExpenseModalTrigger] = useState(0);
  // Incremented when a rejected report is reopened -- tells ExpenseReport to reload
  const [expenseReloadTrigger, setExpenseReloadTrigger] = useState(0);

  const showToast = useCallback((message, type = "default") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Called from ReportHistory when the user reopens a rejected report for editing.
  const handleReopenReport = useCallback(() => {
    setActiveTab("submit");
    setExpenseReloadTrigger((t) => t + 1);
  }, []);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(console.error);
  };

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <ToastContext.Provider value={showToast}>
      <div className="app">
        {/* Header */}
        <header className="header">
          <div>
            <div className="header-title">The Lake Companies Expense Report App</div>
            <div className="header-sub">{user?.name || user?.username}</div>
          </div>
          <button className="btn-icon" onClick={handleLogout} title="Sign out">
            ⎋
          </button>
        </header>

        {/* Main content */}
        <main className="main-content">
          {activeTab === "submit"  && (
            <ExpenseReport
              user={user}
              triggerModal={expenseModalTrigger}
              reloadTrigger={expenseReloadTrigger}
            />
          )}
          {activeTab === "history" && (
            <ReportHistory user={user} onReopenReport={handleReopenReport} />
          )}
          {activeTab === "approve" && isApprover && <ApprovalView />}
          {activeTab === "images" && <MyImages />}
        </main>

        {/* Bottom nav */}
        <nav className="tab-nav">
          <button
            className={`tab-btn${activeTab === "submit" ? " active" : ""}`}
            onClick={() => { setActiveTab("submit"); setExpenseModalTrigger((t) => t + 1); }}
          >
            <span className="tab-icon">➕</span>
            New Expense
          </button>
          <button
            className={`tab-btn${activeTab === "history" ? " active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <span className="tab-icon">📋</span>
            My Reports
          </button>
          {isApprover && (
            <button
              className={`tab-btn${activeTab === "approve" ? " active" : ""}`}
              onClick={() => setActiveTab("approve")}
            >
              <span className="tab-icon">✅</span>
              Approvals
            </button>
          )}
          <button
            className={`tab-btn${activeTab === "images" ? " active" : ""}`}
            onClick={() => setActiveTab("images")}
          >
            <span className="tab-icon">🖼️</span>
            My Images
          </button>
        </nav>

        {/* Toast */}
        {toast && <Toast message={toast.message} type={toast.type} />}
      </div>
    </ToastContext.Provider>
  );
}